import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { useDispatch } from 'src/store/useDispatch';
import { useSelector } from 'react-redux';

import PagePanel from 'src/components/PagePanel';
import { Table, type TableSort } from 'src/components/TableNew';
import { CopyableCell } from 'src/components/CopyableCell';
import PageHeader from 'src/components/PageHeader';
import NoResult from 'src/components/NoResult/NoResult';
import { FilterBar, type FilterDef } from 'src/components/FilterBar';
import { useFilterQueryEffect } from 'src/hooks/useFilterQueryEffect';
import { useResetPageParam } from 'src/hooks/useResetPageParam';

import { Option, fetchCurrencyOptions } from 'src/api/reference';
import type { ReviewQueueKind } from 'src/api/adminCompliance';
import {
  TRANSACTIONS_HEADERS,
  TRANSACTION_KIND_OPTIONS,
  TRANSACTION_STATUS_OPTIONS,
} from 'src/utils/constants';
import { fetchTransactions, resetFilters, updateFilter } from 'src/store/slice/transactions';
import TransactionDetailsModal from './TransactionDetailsModal';
import CasesTab from './CasesTab';
import ReviewQueueTab from './ReviewQueueTab';
import { TransactionTab, TransactionTabs } from './styled';

type TransactionsTab = 'all' | 'deposit' | 'withdraw' | 'cases';

// The two queue tabs read the compliance review queues — the transactions
// screening has frozen and that are waiting on a decision. They used to be a
// plain `kind` filter over every deposit or withdrawal, which is what the All
// tab's Kind picker already does.
const TABS: { id: TransactionsTab; label: string; queue?: ReviewQueueKind }[] = [
  { id: 'all', label: 'Transactions' },
  { id: 'deposit', label: 'Deposit Review Queue', queue: 'deposits' },
  { id: 'withdraw', label: 'Withdrawal Review Queue', queue: 'withdrawals' },
  { id: 'cases', label: 'Cases' },
];

// One bar per filter; the API ANDs kind, status and currency_code. Address and
// Transaction ID both travel through the API's single `field`/`value` pair, so
// they are mutually exclusive: typing in one clears the other (see onChange).
const TRANSACTION_FILTERS: FilterDef[] = [
  { key: 'kind', label: 'Kind', options: TRANSACTION_KIND_OPTIONS, allLabel: 'All kinds' },
  { key: 'status', label: 'Status', options: TRANSACTION_STATUS_OPTIONS, allLabel: 'All statuses' },
  { key: 'currency_code', label: 'Currency', options: [], allLabel: 'All currencies' },
  { key: 'address', label: 'Recipient address', placeholder: 'Starts with…' },
  { key: 'id', label: 'Transaction ID', placeholder: 'Exact id' },
];

const EXCLUSIVE_SEARCH: Record<string, string> = { address: 'id', id: 'address' };

const Transactions = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<TransactionsTab>('all');
  // Set by whichever embedded tab is showing, so the header count follows it.
  const [embeddedTotal, setEmbeddedTotal] = useState<number | null>(null);
  const [currencyOptions, setCurrencyOptions] = useState<Option[]>([]);
  const [sort, setSort] = useState<TableSort>(null);
  const resetPageParam = useResetPageParam();

  const dispatch = useDispatch();
  const {
    data: { transactions, totalCount, totalPages, filters },
    loading,
  } = useSelector(({ transactions }) => transactions);

  const activeQueue = TABS.find((tab) => tab.id === activeTab)?.queue;
  const isCases = activeTab === 'cases';
  // Everything but the All tab fetches its own data and owns its own toolbar.
  const isEmbedded = isCases || Boolean(activeQueue);
  const items = transactions ?? [];

  const headerCount = isEmbedded ? embeddedTotal : totalCount;
  const title =
    headerCount == null ? 'Transactions' : `Transactions (${headerCount})`;

  const transactionFilters = useMemo(
    () =>
      TRANSACTION_FILTERS.map((filter) =>
        filter.key === 'currency_code' ? { ...filter, options: currencyOptions } : filter,
      ),
    [currencyOptions],
  );

  const runQuery = useCallback(
    (params: { page: number; filters: Record<string, string> }) => {
      // Embedded tabs fetch their own data; the All tab's query stays parked
      // until the admin comes back to it.
      if (isEmbedded) return;
      dispatch(
        fetchTransactions({
          page: params.page,
          filters: params.filters,
          sort: sort?.field,
          order: sort?.direction,
        }),
      );
    },
    // `sort` belongs to the query, so a change re-runs it from page 1.
    [dispatch, isEmbedded, sort],
  );

  const { refresh: refreshQuery } = useFilterQueryEffect({ filters, currentPage, setCurrentPage, query: runQuery });

  useEffect(() => {
    let active = true;
    fetchCurrencyOptions().then((options) => {
      if (active) setCurrencyOptions(options);
    });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      dispatch(resetFilters());
    };
  }, [dispatch]);

  const handleFilterChange = (key: string, value: string) => {
    dispatch(updateFilter({ key, value }));
    // Only one of address / id can reach the API, so the other is emptied
    // rather than silently ignored.
    const other = EXCLUSIVE_SEARCH[key];
    if (other && value && filters[other]) {
      dispatch(updateFilter({ key: other, value: '' }));
    }
  };

  const handleTabChange = (tab: TransactionsTab) => {
    if (tab === activeTab) return;

    setActiveTab(tab);
    setCurrentPage(1);
    // Pagination reads the page from the URL, so local state alone would be
    // overwritten straight back to the page the previous tab was on.
    resetPageParam();

    // Each tab starts from a clean slate: a filter carried over from another
    // tab is invisible on arrival and may not even be valid for the new dataset.
    setSort(null);
    dispatch(resetFilters());

    setEmbeddedTotal(null);
  };

  return (
    <PagePanel>
      <PageHeader title={title} hideSearch />
      <TransactionTabs>
        {TABS.map((tab) => (
          <TransactionTab
            key={tab.id}
            type="button"
            $active={activeTab === tab.id}
            onClick={() => handleTabChange(tab.id)}
          >
            {tab.label}
          </TransactionTab>
        ))}
      </TransactionTabs>

      {isEmbedded ? null : (
        <FilterBar
          filters={transactionFilters}
          values={filters}
          onChange={handleFilterChange}
          onClear={() => dispatch(resetFilters())}
          onRefresh={refreshQuery}
        />
      )}

      {isCases ? (
        <CasesTab onTotalChange={setEmbeddedTotal} />
      ) : activeQueue ? (
        <ReviewQueueTab kind={activeQueue} onTotalChange={setEmbeddedTotal} />
      ) : items.length > 0 || loading ? (
        <Table
          loading={loading}
          headers={TRANSACTIONS_HEADERS}
          items={items}
          columns="0.9fr 0.9fr 1.4fr 0.9fr"
          fieldsToShow={['currency_code', 'type', 'address', 'status']}
          // The API can only order by these two; the others have no index.
          sortableFields={['type', 'status']}
          sort={sort}
          onSortChange={setSort}
          totalPages={totalPages}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          renderCell={(item, cellField) =>
            cellField === 'address' ? (
              <CopyableCell value={item.address} label="Recipient address" />
            ) : null
          }
          renderDetailsModal={(item, onClose) => (
            <TransactionDetailsModal transaction={item} onClose={onClose} />
          )}
        />
      ) : (
        <NoResult />
      )}
    </PagePanel>
  );
};

export default Transactions;
